import { createEffect, on, Show, createSignal } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { type ElementNode, activeElement } from "@solidtv/solid";
import { useFocusStack, LazyRow, Column } from "@solidtv/solid/primitives";
import { getManifestUrl, getPosterForWidth, type JWPlaylistItem, type JWSeason, fetchSeriesSeasons, fetchMedia } from "../api/jw";
import { playerState, setPlayerState } from "../overlayState";
import styles from "../styles";
import theme from "theme";

import EntityBackground from "./EntityBackground";
import EntityHero from "./EntityHero";
import EpisodeCard from "./EpisodeCard";
import SeasonTab from "./SeasonTab";
import { For } from "solid-js";
import { Row } from "@solidtv/solid/primitives";

interface JWAssetPanelProps {
  item: JWPlaylistItem | null;
  open: boolean;
  close: () => boolean;
}

export default function JWAssetPanel(props: JWAssetPanelProps) {
  const { storeFocus, restoreFocus } = useFocusStack();
  const [seasons, setSeasons] = createSignal<JWSeason[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [selectedSeasonIdx, setSelectedSeasonIdx] = createSignal(0);
  const navigate = useNavigate();

  let actionRef!: ElementNode;
  let panelRef!: ElementNode;
  let seasonsRowRef!: ElementNode;
  let episodesRowRef!: ElementNode;

  let wasPlaying = false;
  createEffect(
    on(playerState, (state) => {
      if (state) {
        wasPlaying = true;
      } else if (wasPlaying && props.open) {
        wasPlaying = false;
        closePanel();
      }
    }, { defer: true })
  );

  createEffect(
    on(
      () => props.open,
      async (isOpen) => {
        if (isOpen && props.item) {
          storeFocus(activeElement() as ElementNode);

          panelRef?.animate({ x: 0 }, { duration: 400, easing: "ease-in-out" }).start();

          if (props.item.contentType === "series" || props.item.playlist) {
            setLoading(true);
            try {
              const fetchedSeasons = await fetchSeriesSeasons(props.item);
              setSeasons(fetchedSeasons || []);
              setSelectedSeasonIdx(Math.max(0, (fetchedSeasons || []).length - 1));
            } catch (err) {
              setSeasons([]);
            } finally {
              setLoading(false);
            }
          } else {
            setSeasons([]);
          }

          queueMicrotask(() => {
            if (actionRef) {
              actionRef.setFocus();
            }
          });
        } else if (panelRef && panelRef.rendered) {
          panelRef?.animate({ x: theme.layout.screenW }, { duration: 400, easing: "ease-in-out" }).start();
        }
      },
      { defer: true }
    )
  );

  function closePanel() {
    panelRef?.animate({ x: theme.layout.screenW }, { duration: 400, easing: "ease-in-out" }).start();
    setTimeout(() => {
      props.close();
      restoreFocus();
    }, 400);
    return true;
  }

  async function onPlay() {
    if (!props.item) return;

    let manifestUrl = getManifestUrl(props.item.sources);

    let itemTracks = props.item.tracks;

    if (!manifestUrl && props.item.mediaid && props.item.contentType !== "series") {
      try {
        const mediaData = await fetchMedia(props.item.mediaid);
        if (mediaData && mediaData.playlist && mediaData.playlist.length > 0) {
          manifestUrl = getManifestUrl(mediaData.playlist[0].sources);
          if (!itemTracks) itemTracks = mediaData.playlist[0].tracks;
        }
      } catch (err) {
        console.error("Failed to fetch media for item", err);
      }
    }

    if (manifestUrl) {
      setPlayerState({
        manifestUrl,
        title: props.item.title,
        isLive: props.item.content_source_type === "live_bcl" || false,
        tracks: itemTracks
      });
      return;
    }

    const selectedSeason = seasons()[selectedSeasonIdx()];
    if (selectedSeason && selectedSeason.episodes.length > 0) {
      const firstEpItem = selectedSeason.episodes[0].item;
      let epManifestUrl = getManifestUrl(firstEpItem.sources);
      let epTracks = firstEpItem.tracks;

      if (!epManifestUrl && firstEpItem.mediaid) {
        try {
          const mediaData = await fetchMedia(firstEpItem.mediaid);
          if (mediaData && mediaData.playlist && mediaData.playlist.length > 0) {
            epManifestUrl = getManifestUrl(mediaData.playlist[0].sources);
            if (!epTracks) epTracks = mediaData.playlist[0].tracks;
          }
        } catch (err) {
        }
      }

      if (epManifestUrl) {
        setPlayerState({
          manifestUrl: epManifestUrl,
          title: firstEpItem.title,
          isLive: firstEpItem.content_source_type === "live_bcl" || false,
          tracks: epTracks
        });
      }
    }
  }

  async function onEpisodePlay() {
    const elm = activeElement() as any;
    if (!elm?.item) return;

    const tileItem = elm.item.item as JWPlaylistItem;
    let manifestUrl = getManifestUrl(tileItem.sources);
    let tileTracks = tileItem.tracks;

    if (!manifestUrl && tileItem.mediaid) {
      try {
        const mediaData = await fetchMedia(tileItem.mediaid);
        if (mediaData && mediaData.playlist && mediaData.playlist.length > 0) {
          manifestUrl = getManifestUrl(mediaData.playlist[0].sources);
          if (!tileTracks) tileTracks = mediaData.playlist[0].tracks;
        }
      } catch (err) {
      }
    }

    if (manifestUrl) {
      setPlayerState({
        manifestUrl,
        title: tileItem.title,
        isLive: tileItem.content_source_type === "live_bcl" || false,
        tracks: tileTracks
      });
    }
  }

  function getBackdropForWidth(item: JWPlaylistItem, width: number): string {
    return getPosterForWidth(item, width);
  }

  const hasPlayableSource = () => {
    if (!props.item) return false;
    if (props.item.sources && getManifestUrl(props.item.sources)) return true;
    if (seasons().length > 0) return true;
    if (props.item.mediaid && props.item.contentType !== "series") return true;
    return false;
  };

  const durationText = () => {
    if (!props.item?.duration || props.item.duration <= 0) return "";
    const mins = Math.floor(props.item.duration / 60);
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    if (hrs > 0) return `${hrs}h ${remainMins < 10 ? "0" : ""}${remainMins}min`;
    return `${mins} min`;
  };

  const currentEpisodes = () => {
    const seasonList = seasons();
    if (seasonList.length === 0 || !seasonList[selectedSeasonIdx()]) return [];
    return seasonList[selectedSeasonIdx()].episodes;
  };

  return (
    <Show when={props.item}>
      <view
        ref={panelRef!}
        x={theme.layout.screenW}
        y={0}
        width={theme.layout.screenW}
        height={1080}
        zIndex={200}
        color={0x000000ff}
        onBack={closePanel}
        onBackspace={closePanel}
        onEscape={closePanel}
      >
        <EntityBackground
          poster={getPosterForWidth(props.item!, 640)}
          backdrop={getBackdropForWidth(props.item!, 1920)}
        />

        <EntityHero
          title={props.item!.title}
          description={props.item!.description}
          durationText={durationText()}
          tags={props.item!.tags}
          hasPlayableSource={hasPlayableSource()}
          isLive={props.item!.content_source_type === "live_bcl"}
          onPlay={onPlay}
          onClose={closePanel}
          actionRef={(el) => actionRef = el}
          onDown={() => {
            if (seasons().length > 1 && seasonsRowRef) {
              seasonsRowRef.setFocus();
              return true;
            }
            if (currentEpisodes().length > 0 && episodesRowRef) {
              episodesRowRef.setFocus();
              return true;
            }
            return false;
          }}
        />

        {/* Episodes & Seasons Section */}
        <Show when={loading()}>
          <text x={120} y={650} fontSize={28} color={theme.textSecondary} zIndex={110}>
            Loading seasons...
          </text>
        </Show>

        <Show when={!loading() && currentEpisodes().length > 0}>
          <text x={120} y={650} fontSize={32} fontWeight="bold" color={theme.textPrimary} zIndex={110}>
            Episodes
          </text>
        </Show>

        <Show when={!loading() && seasons().length > 1}>
          <view x={120} y={700} zIndex={110} forwardFocus={0}>
            <Row
              ref={seasonsRowRef!}
              gap={20}
              height={60}
              scroll="none"
              onUp={() => {
                if (actionRef) {
                  actionRef.setFocus();
                  return true;
                }
                return false;
              }}
              onDown={() => {
                if (currentEpisodes().length > 0 && episodesRowRef) {
                  episodesRowRef.setFocus();
                  return true;
                }
                return false;
              }}
            >
              <For each={seasons()}>
                {(season, index) => (
                  <SeasonTab
                    isSelected={selectedSeasonIdx() === index()}
                    onEnter={() => setSelectedSeasonIdx(index())}
                  >
                    {season.title}
                  </SeasonTab>
                )}
              </For>
            </Row>
          </view>
        </Show>

        <Show when={!loading() && currentEpisodes().length > 0}>
          <view x={120} y={seasons().length > 1 ? 765 : 700} width={1680} height={200} zIndex={110} forwardFocus={0}>
            <LazyRow
              ref={episodesRowRef!}
              gap={30}
              upCount={5}
              scroll="center"
              each={currentEpisodes()}
              onUp={() => {
                if (seasons().length > 1 && seasonsRowRef) {
                  seasonsRowRef.setFocus();
                  return true;
                }
                if (actionRef) {
                  actionRef.setFocus();
                  return true;
                }
                return false;
              }}
            >
              {(episodeP) => (
                <EpisodeCard
                  item={episodeP()}
                  onEnter={onEpisodePlay}
                />
              )}
            </LazyRow>
          </view>
        </Show>
      </view>
    </Show>
  );
}
